import React from 'react';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import PropTypes from 'prop-types';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import { buildVinNote } from '../OperatingValues/utils.js';

import { SCHEMATICS } from './constants/schematics';
import { ATTRIBUTES } from './constants/components';
import { COLORS } from '../../styles/constants.js';
import { useTranslation } from 'react-i18next';
import { generateInputParamsAttributesPdf } from '../InputParameters/constants';
import { fixUnrecognizedChar } from '../OperatingValues/utils.js';
import { logEventType, middlePartOfRedirectLink } from '../../utils/analytics/adobe/adob';
import queryString from 'query-string';
import pushToGTM, { CLICK_DATASHEET_GA4_TAG } from '../../utils/analytics/gtm';

const Schematic = ({ schematic, page, itemId }) => {
	const { t } = useTranslation();
	const schematicSvg = SCHEMATICS[schematic] ? SCHEMATICS[schematic].svg : null;
	const partNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const operatingValues = useSelector(state => state.selectedDesign.detail.opValueCategories);
	const bom = useSelector(state => state.selectedDesign.detail.bom);
	const schematicComponents = SCHEMATICS[schematic] ? SCHEMATICS[schematic].components : null;
	const inputParametersSD = useSelector(state => state.selectedDesign.detail.inputParameters);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const initialVinNom = useSelector(state => state.selectedDesign.initialInputParameters.vinNom);
	const topology = useSelector(state => state.selectedDesign.topology);
	const topologyCategory = useSelector(state => state.selectedDesign.detail.rankingParameters.topologyCategory);
	const datasheetLink = useSelector(state => state.selectedDesign.detail.rankingParameters.datasheetUrl);
	const domain = window.location.origin;
	const fullURL = window.location.href;
	const path = window.location.pathname;

	const attributes = generateInputParamsAttributesPdf(inputParametersSD.supplyType);
	const attributeMappings = {};

	attributes.forEach(attr => {
		attributeMappings[attr.value] = { label: attr.formattedLabel, unit: attr.unit };
	});

	const formatSVGString = str => {
		return str
			.replace(/<sub>/g, '<tspan dy="0.5em" font-size="0.7em">')
			.replace(/<\/sub>/g, '</tspan>')
			.replace(/<sup>/g, '<tspan dy="-0.5em" font-size="0.7em">')
			.replace(/<\/sup>/g, '</tspan>')
			.replace(/<i>/g, '<tspan font-style="italic">')
			.replace(/<\/i>/g, '</tspan>');
	};

	const containsSubscriptOrSuperscript = str => /<sub>|<sup>/.test(str);

	const createLink = (partNumber, link, manufacturer) => {
		const isNotGeneric = 'Generic' !== manufacturer;

		const datasheetTrackingParameters = {
			redirectLink: link,
			partNumber: partNumber,
			inputParameters: JSON.stringify(inputParametersDL),
			buttonName: partNumber,
			wdtTabName: 'design',
			fullURL: fullURL,
			path: path,
			logEventType: logEventType.DATASHEETLOG
		};
		const redirectLink = domain + middlePartOfRedirectLink + queryString.stringify(datasheetTrackingParameters);

		const valueTspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
		if (isNotGeneric) {
			valueTspan.setAttribute('fill', '#0000EE');
		}
		valueTspan.textContent = partNumber;

		const anchor = document.createElementNS('http://www.w3.org/2000/svg', 'a');
		anchor.setAttribute('href', redirectLink);
		anchor.setAttribute('target', '_blank');

		if (isNotGeneric) {
			anchor.appendChild(valueTspan);
			return anchor;
		} else {
			return valueTspan;
		}
	};

	const schematicValues = new Map();

	function processSchematicComponents(code) {
		// For schematic svg cleaning
		/*
        let pp = new DOMParser();
        let ss = pp.parseFromString(code, 'image/svg+xml');

        console.log(
            ss.documentElement.outerHTML.replace(/st(\d+)/g, '$&_cp_n4')
        );
        */
		if (page === 'SELECT') {
			return code;
		}

		schematicValues.set('schematic-OPN', createLink(partNumber, datasheetLink, 'onsemi'));

		// iterate through input parameters
		Object.keys(inputParametersSD).forEach(key => {
			if (attributeMappings[key]) {
				const { label, unit } = attributeMappings[key];
				const value = inputParametersSD[key];
				const formattedLabel = formatSVGString(label);
				const dy = containsSubscriptOrSuperscript(label) ? '-0.5em' : '0em';
				const formattedValue = `${formattedLabel} <tspan dy="${dy}"> = ${value}</tspan> ${unit}`;
				schematicValues.set(key, formattedValue);
			}
		});

		// iterate through operating values
		operatingValues[0].operatingValues.forEach(param => {
			const formattedLabel = formatSVGString(param.label);
			const dy = containsSubscriptOrSuperscript(param.label) ? '-0.5em' : '0em';
			const formattedValue = `${formattedLabel} <tspan dy="${dy}"> = ${fixUnrecognizedChar(param.value)}</tspan>`;
			schematicValues.set(param.name, formattedValue);
		});

		schematicComponents.forEach(schemComponent => {
			const bomFixed = bom.map(item => Object.assign({}, item, { selected: false }));

			bomFixed.forEach(item => {
				const parts = item.part.split('<sub>');
				if (parts[1]) {
					parts[1] = parts[1].replace(/<\/sub>/, '').toLowerCase();
				}
				item.part = parts.join('');
			});

			const bomComponent = bomFixed.find(c => c.part === schemComponent.part);

			if (bomComponent) {
				let attributeSymbol;

				if (schemComponent.showPartNumber) {
					schematicValues.set(
						schemComponent.part + '-OPN',
						createLink(bomComponent.partNumber, bomComponent.datasheetLink, bomComponent.manufacturer)
					);
				}

				if (schemComponent.showQuantity && bomComponent.quantity > 1) {
					schematicValues.set(bomComponent.part + '-Qty', 'Qty=' + bomComponent.quantity);
				}

				schemComponent.attributes.forEach(schemAttribute => {
					const bomAttribute = bomComponent.attributes.find(att => att.name === schemAttribute);

					if (bomAttribute) {
						attributeSymbol = ATTRIBUTES[bomAttribute.name];
						const formattedLabel = formatSVGString(bomAttribute.value.label);
						const dy = containsSubscriptOrSuperscript(bomAttribute.value.label) ? '-0.5em' : '0em';
						const formattedValue = `${formattedLabel} <tspan dy="${dy}"> = ${bomAttribute.value.value}${
							bomAttribute.value.unit ? ` ${bomAttribute.value.unit}` : ''
						}</tspan>`;
						schematicValues.set(schemComponent.part + '-' + attributeSymbol, formattedValue);
					}
				});
			}
		});

		const parser = new DOMParser();
		const svgDoc = parser.parseFromString(code, 'image/svg+xml');

		for (const [elementId, value] of schematicValues) {
			const svgElement = svgDoc.getElementById(elementId);
			if (svgElement) {
				if (svgElement.firstChild) {
					svgElement.removeChild(svgElement.firstChild);
				}
				if (elementId.includes('OPN')) {
					svgElement.appendChild(value);
				} else {
					const tspanElement = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
					tspanElement.innerHTML = value;
					svgElement.appendChild(tspanElement);
				}
			}
		}

		return svgDoc.documentElement.outerHTML;
	}

	if (!schematicSvg) {
		return <span></span>;
	}

	const addEventListenersForDatasheetLog = () => {
		// Add event listeners to each element
		for (const [elementId, value] of schematicValues) {
			const valueStr = value.outerHTML; // Get the HTML string of the SVG element
			if (elementId.includes('OPN') && valueStr.includes('logEventType=DatasheetLog')) {
				const element = document.getElementById(elementId + '__des');
				if (element) {
					element.addEventListener('click', logDatasheetDownloadByGTM);
				}
			}
		}
	};

	// Function to handle click events
	const logDatasheetDownloadByGTM = () => {
		pushToGTM(CLICK_DATASHEET_GA4_TAG);
	};

	return (
		<>
			<SVG
				id={(page === 'DESIGN' || page === 'SUMMARY') && 'schematic_el'}
				loader={() => <CircularProgress color="primary" />}
				preProcessor={code => processSchematicComponents(code)}
				onLoad={addEventListenersForDatasheetLog}
				src={schematicSvg}
				uniqueHash={page === 'SELECT' ? 'sel' + itemId : page === 'DESIGN' ? 'des' : 'sum'}
				uniquifyIDs={true}
				width="100%"
				height="100%"
			/>
			{page !== 'SELECT' ? (
				<>
					<Typography variant="caption" color="textSecondary">
						{buildVinNote(inputParametersSD, initialVinNom, topology, topologyCategory)}
					</Typography>
					<br />
					<Typography variant="caption" color="textSecondary">
						**
						<span style={{ backgroundColor: COLORS.PRIMARY, color: COLORS.GUMETAL }}>
							{t('components.schematic.footer.middle')}
						</span>
						{t('components.schematic.footer.end')}
					</Typography>
					<Typography variant="caption" style={{ visibility: 'hidden' }}>
						Invisible text so CN schema is bigger.
					</Typography>
				</>
			) : null}
		</>
	);
};

Schematic.propTypes = {
	icPartNumber: PropTypes.string.isRequired,
	schematic: PropTypes.string.isRequired,
	page: PropTypes.string.isRequired,
	itemId: PropTypes.number
};

export default Schematic;
